import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Box,
  Chip,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { RootState } from '../../store';
import {
  setOperationDialogOpen,
  setOperationValidation,
  setSelectedOperation,
} from '../../store/reducers/operationsSlice';
import { WorkInstruction, ToolFixture, OperationInput } from '../../services/operationsApi';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  multiSelectChip: {
    margin: theme.spacing(0.25),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
}));

const OperationsEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  
  const {
    operations: { selectedOperation, validation },
    dropdowns: {
      operationTypes,
      machines,
      parts,
      toolFixtures,
      workInstructions,
      inputs,
      loading: dropdownLoading,
    },
    ui: { operationDialogOpen },
  } = useSelector((state: RootState) => state.operations);

  const [formData, setFormData] = useState({
    OperationNum: 0,
    Name: '',
    Description: '',
    PartId: '',
    OperationTypeId: '',
    MachineId: '',
    ApprovalStampReqd: false,
    PassOffReqd: false,
    Active: true,
    selectedToolKeys: [] as string[],
    selectedWIKeys: [] as string[],
    selectedInputKeys: [] as string[],
  });

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Initialize form data when operation is selected
  useEffect(() => {
    if (selectedOperation) {
      setFormData({
        OperationNum: selectedOperation.OperationNum || 0,
        Name: selectedOperation.Name || '',
        Description: selectedOperation.Description || '',
        PartId: selectedOperation.PartId || '',
        OperationTypeId: selectedOperation.OperationTypeId || '',
        MachineId: selectedOperation.MachineId || '',
        ApprovalStampReqd: selectedOperation.ApprovalStampReqd || false,
        PassOffReqd: selectedOperation.PassOffReqd || false,
        Active: selectedOperation.Active ?? true,
        selectedToolKeys: selectedOperation.ToolFixtures?.map(tf => tf.Id) || [],
        selectedWIKeys: selectedOperation.WorkInstructions?.map(wi => wi.Id) || [],
        selectedInputKeys: selectedOperation.Inputs?.map(input => input.Id) || [],
      });
    } else {
      // Reset form for new operation
      setFormData({
        OperationNum: 0,
        Name: '',
        Description: '',
        PartId: '',
        OperationTypeId: '',
        MachineId: '',
        ApprovalStampReqd: false,
        PassOffReqd: false,
        Active: true,
        selectedToolKeys: [],
        selectedWIKeys: [],
        selectedInputKeys: [],
      });
    }
    setLocalErrors({});
    dispatch(setOperationValidation({}));
  }, [selectedOperation, dispatch]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific errors
    if (localErrors[field]) {
      setLocalErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (validation[field]) {
      dispatch(setOperationValidation({ ...validation, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.OperationNum || formData.OperationNum <= 0) {
      errors.OperationNum = 'Operation Number is required and must be greater than 0';
    }

    if (!formData.Name.trim()) {
      errors.Name = 'Operation Name is required';
    }

    if (!formData.PartId) {
      errors.PartId = 'Part is required';
    }

    if (!formData.OperationTypeId) {
      errors.OperationTypeId = 'Operation Type is required';
    }

    if (!formData.MachineId) {
      errors.MachineId = 'Machine is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const operationData = {
      ...formData,
      Id: selectedOperation?.Id,
    };

    if (selectedOperation) {
      dispatch({ type: 'operations/updateOperation', payload: operationData });
    } else {
      dispatch({ type: 'operations/createOperation', payload: operationData });
    }
  };

  const handleClose = () => {
    dispatch(setOperationDialogOpen(false));
    dispatch(setSelectedOperation(null));
    dispatch(setOperationValidation({}));
    setLocalErrors({});
  };

  const getFieldError = (field: string): string => {
    return localErrors[field] || validation[field] || '';
  };

  if (dropdownLoading) {
    return (
      <Dialog open={operationDialogOpen} maxWidth="md" fullWidth>
        <DialogContent>
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: 16 }}>
              Loading form data...
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={operationDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedOperation ? 'Edit Operation' : 'Add New Operation'}
      </DialogTitle>
      <DialogContent>
        <form className={classes.form}>
          {/* General Errors */}
          {validation.general && (
            <Typography variant="body2" className={classes.errorText}>
              {validation.general}
            </Typography>
          )}

          {/* Basic Information */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Operation Number"
                  type="number"
                  value={formData.OperationNum}
                  onChange={(e) => handleInputChange('OperationNum', parseInt(e.target.value, 10) || 0)}
                  error={Boolean(getFieldError('OperationNum'))}
                  helperText={getFieldError('OperationNum')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Operation Name"
                  value={formData.Name}
                  onChange={(e) => handleInputChange('Name', e.target.value)}
                  error={Boolean(getFieldError('Name'))}
                  helperText={getFieldError('Name')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.Description}
                  onChange={(e) => handleInputChange('Description', e.target.value)}
                  error={Boolean(getFieldError('Description'))}
                  helperText={getFieldError('Description')}
                />
              </Grid>
            </Grid>
          </div>

          {/* Dependencies */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Dependencies
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={Boolean(getFieldError('PartId'))}>
                  <InputLabel required>Part</InputLabel>
                  <Select
                    value={formData.PartId}
                    onChange={(e) => handleInputChange('PartId', e.target.value)}
                  >
                    {parts.map((part) => (
                      <MenuItem key={part.Id} value={part.Id}>
                        {part.Name || part.Number}
                      </MenuItem>
                    ))}
                  </Select>
                  {getFieldError('PartId') && (
                    <FormHelperText>{getFieldError('PartId')}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={Boolean(getFieldError('OperationTypeId'))}>
                  <InputLabel required>Operation Type</InputLabel>
                  <Select
                    value={formData.OperationTypeId}
                    onChange={(e) => handleInputChange('OperationTypeId', e.target.value)}
                  >
                    {operationTypes.map((type) => (
                      <MenuItem key={type.Id} value={type.Id}>
                        {type.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  {getFieldError('OperationTypeId') && (
                    <FormHelperText>{getFieldError('OperationTypeId')}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={Boolean(getFieldError('MachineId'))}>
                  <InputLabel required>Machine</InputLabel>
                  <Select
                    value={formData.MachineId}
                    onChange={(e) => handleInputChange('MachineId', e.target.value)}
                  >
                    {machines.map((machine) => (
                      <MenuItem key={machine.Id} value={machine.Id}>
                        {machine.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  {getFieldError('MachineId') && (
                    <FormHelperText>{getFieldError('MachineId')}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </div>

          {/* Related Items */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Related Items
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={workInstructions}
                  getOptionLabel={(option: WorkInstruction) => option.Name || ''}
                  value={workInstructions.filter((wi: WorkInstruction) => formData.selectedWIKeys.includes(wi.Id))}
                  onChange={(_event: any, newValue: WorkInstruction[]) => {
                    handleInputChange('selectedWIKeys', newValue.map((wi: WorkInstruction) => wi.Id));
                  }}
                  renderTags={(value: WorkInstruction[], getTagProps: (args: { index: number }) => any) =>
                    value.map((option: WorkInstruction, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option.Name}
                        {...getTagProps({ index })}
                        className={classes.multiSelectChip}
                        key={option.Id}
                      />
                    ))
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Work Instructions"
                      placeholder="Select work instructions..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={toolFixtures}
                  getOptionLabel={(option: ToolFixture) => option.Name || ''}
                  value={toolFixtures.filter((tf: ToolFixture) => formData.selectedToolKeys.includes(tf.Id))}
                  onChange={(_event: any, newValue: ToolFixture[]) => {
                    handleInputChange('selectedToolKeys', newValue.map((tf: ToolFixture) => tf.Id));
                  }}
                  renderTags={(value: ToolFixture[], getTagProps: (args: { index: number }) => any) =>
                    value.map((option: ToolFixture, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option.Name}
                        {...getTagProps({ index })}
                        className={classes.multiSelectChip}
                        key={option.Id}
                      />
                    ))
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Tools & Fixtures"
                      placeholder="Select tools and fixtures..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={inputs}
                  getOptionLabel={(option: OperationInput) => option.Name || ''}
                  value={inputs.filter((input: OperationInput) => formData.selectedInputKeys.includes(input.Id))}
                  onChange={(_event: any, newValue: OperationInput[]) => {
                    handleInputChange('selectedInputKeys', newValue.map((input: OperationInput) => input.Id));
                  }}
                  renderTags={(value: OperationInput[], getTagProps: (args: { index: number }) => any) =>
                    value.map((option: OperationInput, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option.Name}
                        {...getTagProps({ index })}
                        className={classes.multiSelectChip}
                        key={option.Id}
                      />
                    ))
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Operation Inputs"
                      placeholder="Select operation inputs..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>

          {/* Settings */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.ApprovalStampReqd}
                      onChange={(e) => handleInputChange('ApprovalStampReqd', e.target.checked)}
                    />
                  }
                  label="Approval Stamp Required"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.PassOffReqd}
                      onChange={(e) => handleInputChange('PassOffReqd', e.target.checked)}
                    />
                  }
                  label="Pass Off Required"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.Active}
                      onChange={(e) => handleInputChange('Active', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {selectedOperation ? 'Update' : 'Create'} Operation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OperationsEdit; 